/**
 * Phone-native inset grouped list — the "cozy" mobile surface.
 *
 * Modelled on iOS Settings, but the goal is the FEELING rather than the pixel copy: generous,
 * unhurried, obviously drawn for a thumb. The first version of this file was a correct inset list
 * rendered at desktop density (10px radius, 44px rows, bare icons, tight gaps) and it still read as
 * a web page, because density — not structure — is what gives a port away.
 *
 * The numbers below are measured off iOS 18 Settings and each earns its place:
 *
 * - **22px radius.** The app's own scale tops out at 16px, which is a *card* radius. A phone list
 *   uses a noticeably softer corner; at 10px the group reads as a table with rounded edges.
 * - **56px rows** (not the 44px HIG minimum). 44px is the smallest a target may be, not the size a
 *   comfortable list row should be — Settings rows are ~58px and that difference is most of what
 *   "cozy" means here.
 * - **Icon chips**, not bare glyphs. The rounded-square container is one of the most recognisable
 *   things about a phone settings list. iOS tints them per-row; this app is monochrome by decision,
 *   so the chip keeps the SHAPE and drops the colour.
 * - **36px between groups.** Generous separation is what makes groups read as distinct cards rather
 *   than one long table broken by lines.
 * - **Sentence-case section headers.** ALL-CAPS headers are iOS 6–13; modern iOS is sentence case,
 *   and caps read as a web "table header" to anyone who uses a current phone.
 * - **Separator inset to the LABEL**, skipping the chip. The strongest single tell: a full-bleed
 *   rule is a web table wearing a costume.
 *
 * Every mobile size is reset at `md:` so the desktop app keeps its own denser scale untouched.
 */
import * as React from 'react';
import { RightSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';

/** Chip (30px) + gap (12px) — where the label starts, and so where the separator starts. */
const SEPARATOR_INSET_WITH_ICON = 'ml-[58px] md:ml-10';
const SEPARATOR_INSET_PLAIN = 'ml-4';

export interface SettingsGroupProps {
  /** Sentence-case header above the group, as modern iOS uses. */
  header?: React.ReactNode;
  /** Explanatory footnote below the group — iOS's grey caption pattern. */
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export function SettingsGroup({ header, footer, children, className }: SettingsGroupProps) {
  return (
    <section className={cn('mb-9 md:mb-6', className)}>
      {header ? (
        <h2 className="px-4 pb-2 text-[15px] font-normal leading-[20px] text-[var(--tvl-ink-3)] md:px-1 md:pb-1.5 md:text-[11px] md:uppercase md:tracking-wide">
          {header}
        </h2>
      ) : null}
      <div
        className={cn(
          'overflow-hidden border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
          // Softer than anything on the app's own scale — a phone list corner, not a card corner.
          // The border is unconditional now: it was mobile-exempt while the page behind was darker,
          // and with a single-colour surface a borderless group has nothing to separate it.
          'rounded-[22px] md:rounded-[var(--tvl-r3)]',
        )}
      >
        {children}
      </div>
      {footer ? (
        <p className="px-4 pt-2 text-[13px] leading-[18px] text-[var(--tvl-ink-3)] md:px-1 md:text-[12px]">
          {footer}
        </p>
      ) : null}
    </section>
  );
}

export interface SettingsRowProps {
  label: React.ReactNode;
  /** Secondary line under the label, as the Apple Account row uses. */
  detail?: React.ReactNode;
  /** Right-aligned current value before the chevron ("On", a network name). */
  value?: React.ReactNode;
  icon?: React.ReactNode;
  onPress?: () => void;
  chevron?: boolean;
  /** Right-hand control (a switch, a badge) instead of a chevron. */
  accessory?: React.ReactNode;
  /** Suppresses the separator — set on the last row of a group. */
  last?: boolean;
  destructive?: boolean;
  className?: string;
}

export function SettingsRow({
  label,
  detail,
  value,
  icon,
  onPress,
  chevron,
  accessory,
  last,
  destructive,
  className,
}: SettingsRowProps) {
  const showChevron = chevron ?? (!!onPress && !accessory);
  const interactive = !!onPress;

  const body = (
    <>
      {icon ? (
        // The chip. Monochrome by decision, so it carries the shape without iOS's per-row tint.
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            'h-[30px] w-[30px] rounded-[8px] bg-[var(--tvl-inset)] text-[var(--tvl-ink-2)]',
            'md:h-7 md:w-7 md:rounded-none md:bg-transparent',
          )}
        >
          {icon}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            'truncate text-[17px] leading-[22px] md:text-[14px] md:leading-[20px]',
            destructive ? 'text-[var(--tvl-danger)]' : 'text-[var(--tvl-ink)]',
          )}
        >
          {label}
        </span>
        {detail ? (
          <span className="truncate text-[14px] leading-[19px] text-[var(--tvl-ink-3)] md:text-[12px]">
            {detail}
          </span>
        ) : null}
      </span>
      {value !== undefined && value !== null ? (
        <span className="shrink-0 truncate text-[17px] leading-[22px] text-[var(--tvl-ink-3)] md:text-[14px]">
          {value}
        </span>
      ) : null}
      {accessory ?? null}
      {showChevron ? (
        <RightSmallLine size={20} aria-hidden className="-mr-1 shrink-0 text-[var(--tvl-ink-3)]" />
      ) : null}
    </>
  );

  const rowClass = cn(
    'flex w-full items-center gap-3 px-4 text-left',
    // 56px, not the 44px minimum: 44 is the smallest a target may be, not the size a comfortable
    // list row should be.
    'min-h-[56px] py-2.5 md:min-h-0 md:py-1.5',
    interactive && 'active:bg-[var(--tvl-wash-pressed)] md:hover:bg-[var(--tvl-wash-hover)]',
    className,
  );

  const content = interactive ? (
    <button type="button" onClick={onPress} className={rowClass}>
      {body}
    </button>
  ) : (
    <div className={rowClass}>{body}</div>
  );

  return (
    <div className="relative">
      {content}
      {/* Positioned rather than a border-bottom, so it can start at the LABEL. */}
      {last ? null : (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute bottom-0 right-0 block h-px bg-[var(--tvl-hairline)]',
            icon ? SEPARATOR_INSET_WITH_ICON : SEPARATOR_INSET_PLAIN,
          )}
        />
      )}
    </div>
  );
}

/**
 * Page wrapper: the darker grouped background the cards sit on, plus the breathing room around
 * them. Without the darker page the cards have nothing to sit on and the rounded corners do nothing.
 */
export function SettingsPage({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        // NO background and no negative margins. Painting a darker "grouped" page here produced
        // visible BANDS on a phone — the topbar's canvas above this element, the darker page in the
        // middle, canvas again below it wherever content was shorter than the scroll area — because
        // this wrapper only covers its own height, not the scrolling <main> around it. The negative
        // margins used to bleed it edge-to-edge also broke the sticky topbar's containment.
        //
        // The `md:bg-transparent` that was meant to keep desktop clean emitted NOTHING (see
        // PageContainer), so desktop was carrying the band too. One consistent surface instead.
        'pb-10 pt-2',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Large left-aligned page title with the generous space above it that a phone screen expects.
 * iOS gives this a lot of room; cramping it is what makes a screen feel like a dense web view.
 */
export function SettingsTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="px-1 pb-4 pt-3 text-[34px] font-bold leading-[41px] tracking-[-0.5px] text-[var(--tvl-ink)] md:pb-3 md:pt-1 md:text-[24px] md:leading-[32px] md:font-semibold">
      {children}
    </h1>
  );
}
